import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-value';

/**
 * Value-proposition card explaining why a SignNow capability matters.
 * Two-column card layout: heading + paragraph text on the left, supporting image on the right.
 * Pairs prose with a media visual, unlike list- or table-based SignNow blocks that present structured data.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for the value and legal standing of {keyword}. 6-10 words.
 * @slot text - {content} <me-text> - Paragraph on why {keyword} matters: concrete business benefit and compliance outcome under ESIGN/UETA. You MUST write at least 30 words.
 * @slot image - <me-picture>
 * @example
 *   <sn-value>
 *     <me-heading slot="heading" variant="4" tag="h2">Why should you use e-signature</me-heading>
 *     <me-text slot="text">A co-performer release agreement is a legally binding document that defines the terms under which one performer allows another to use their likeness or performance.</me-text>
 *     <me-picture slot="image" alt="Why should you use e-signature" aspect-ratio="715/441" lazy="true" src-set="/signnow/sn-value/dynamic-prompt-1.png 1x, /signnow/sn-value/dynamic-prompt-1@2x.png 2x" webp-src-set="/signnow/sn-value/dynamic-prompt-1.webp 1x, /signnow/sn-value/dynamic-prompt-1@2x.webp 2x"></me-picture>
 *   </sn-value>
 */
@Component({
  tag: 'sn-value',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcPurposeImageRight {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Legacy / raw-HTML fallback. For new usage prefer named slots.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} text - Fallback body text (use slot="text" instead)
   * @property {image} image - Image source base path (use slot="image" instead)
   * @property {string} srcSet - Custom srcSet for the image
   * @property {string} webpSrcSet - Custom WebP srcSet for the image
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        image?: string;
        srcSet?: string;
        webpSrcSet?: string;
        raw_html?: string;
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    const textContent = values?.raw_html ?? values?.text;

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__card`}>
          <div class={`${COMPONENT_NAME}__content`}>
            <slot name="heading">
              {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
            </slot>
            <slot name="text">
              {textContent && <me-text innerHTML={textContent}></me-text>}
            </slot>
          </div>
          <div class={`${COMPONENT_NAME}__image`}>
            <slot name="image">
              {values?.image && (
                <me-picture
                  alt={values?.title ?? ''}
                  aspectRatio="715/441"
                  lazy={true}
                  srcSet={values?.srcSet ?? `${values.image}.png 1x, ${values.image}@2x.png 2x`}
                  webpSrcSet={values?.webpSrcSet ?? `${values.image}.webp 1x, ${values.image}@2x.webp 2x`}
                ></me-picture>
              )}
            </slot>
          </div>
        </div>
      </me-section>
    );
  }
}
